/**
 * Live run event stream (`GET /runs/{uid}/events`) folded into a view model.
 *
 * The server first sends `snapshot` {content, reasoning, status} with what has
 * been produced so far, then live events until a terminal `done` / `error` /
 * `paused`. {@link runStreamReducer} is pure so it can be unit-tested and
 * reused by any surface that renders a run (run log, agent playground…).
 */
import { useEffect, useReducer } from "react"
import { type SubscriptionStatus, subscribeSSE } from "@/api/sse"

export type ToolCallStatus = "running" | "done" | "error"

export interface LiveToolCall {
  id: string
  name: string
  label: string
  kind: string
  args: unknown
  result?: unknown
  isError?: boolean
  durationMs?: number
  status: ToolCallStatus
}

export interface LiveUsage {
  input_tokens: number
  output_tokens: number
  cache_read_tokens: number
  cache_write_tokens: number
}

export type LiveRunStatus =
  | "connecting"
  | "running"
  | "completed"
  | "failed"
  | "cancelled"
  | "paused"
  | "waiting"

/**
 * What happened before the current answer, in order: text the model streamed
 * before calling tools (a `text_reset` moves it here instead of discarding it)
 * and the tool calls themselves.
 */
export type RunTimelineEntry = { type: "text"; text: string } | { type: "tool"; id: string }

export interface RunStreamState {
  status: LiveRunStatus
  /** Text streamed since the last tool call (the answer, once the run is done). */
  content: string
  reasoning: string
  toolCalls: LiveToolCall[]
  timeline: RunTimelineEntry[]
  /** Latest human-readable progress label (`status_step`). */
  step?: string
  usage?: LiveUsage
  model?: string
  cost?: number | null
  steps?: number
  error?: { message: string; code: string }
  /** Number of events the server dropped because we were too slow (`lagged`). */
  skipped: number
  /** True once a terminal event (or a finished snapshot) arrived. */
  finished: boolean
}

export const initialRunStreamState: RunStreamState = {
  status: "connecting",
  content: "",
  reasoning: "",
  toolCalls: [],
  timeline: [],
  skipped: 0,
  finished: false,
}

export interface RunStreamEvent {
  event: string
  data: unknown
}

type Obj = Record<string, unknown>
const obj = (v: unknown): Obj => (v && typeof v === "object" ? (v as Obj) : {})
const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v))
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0)

const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled", "waiting"])

/** Records a tool call in the timeline the first time its id is seen. */
function withCallEntry(state: RunStreamState, id: string): RunTimelineEntry[] {
  return state.toolCalls.some((c) => c.id === id) ? state.timeline : [...state.timeline, { type: "tool", id }]
}

function upsertCall(calls: LiveToolCall[], id: string, patch: Partial<LiveToolCall>): LiveToolCall[] {
  const idx = calls.findIndex((c) => c.id === id)
  if (idx === -1)
    return [
      ...calls,
      { id, name: "", label: "", kind: "", args: {}, status: "running", ...patch } satisfies LiveToolCall,
    ]
  const next = calls.slice()
  const defined = Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined))
  next[idx] = { ...calls[idx]!, ...defined }
  return next
}

export function runStreamReducer(
  state: RunStreamState,
  ev: RunStreamEvent | { event: "reset" },
): RunStreamState {
  if (ev.event === "reset") return initialRunStreamState
  const d = obj((ev as RunStreamEvent).data)
  switch (ev.event) {
    case "snapshot": {
      const status = str(d.status) || "running"
      const finished = TERMINAL_STATUSES.has(status)
      return {
        ...state,
        // a snapshot replaces what we have (it is sent again on reconnect)
        content: str(d.content),
        reasoning: str(d.reasoning),
        status: (status as LiveRunStatus) ?? "running",
        finished: state.finished || finished,
      }
    }
    case "run_started":
      return { ...state, status: "running", model: str(d.model) || state.model }
    case "thinking_delta":
      return { ...state, status: "running", reasoning: state.reasoning + str(d.delta) }
    case "text_delta":
      return { ...state, status: "running", content: state.content + str(d.delta) }
    case "text_reset":
      // the text so far was a preamble to a tool call: keep it, in order, instead of dropping it
      return state.content.trim()
        ? { ...state, content: "", timeline: [...state.timeline, { type: "text", text: state.content }] }
        : { ...state, content: "" }
    case "tool_call":
      return {
        ...state,
        status: "running",
        timeline: withCallEntry(state, str(d.tool_call_id)),
        toolCalls: upsertCall(state.toolCalls, str(d.tool_call_id), {
          name: str(d.name),
          label: str(d.label) || str(d.name),
          kind: str(d.kind),
          args: d.args ?? {},
          status: "running",
        }),
      }
    case "tool_result":
      return {
        ...state,
        toolCalls: upsertCall(state.toolCalls, str(d.tool_call_id), {
          name: str(d.name) || undefined,
          result: d.result,
          isError: !!d.is_error,
          durationMs: num(d.duration_ms),
          status: d.is_error ? "error" : "done",
        }),
      }
    case "skill_loaded":
    case "mcp_loaded":
      return {
        ...state,
        timeline: withCallEntry(state, str(d.tool_call_id)),
        toolCalls: upsertCall(state.toolCalls, str(d.tool_call_id), {
          label: str(d.label),
          status: "done",
        }),
      }
    case "status_step":
      return { ...state, step: str(d.label) }
    case "usage": {
      const prev = state.usage ?? {
        input_tokens: 0,
        output_tokens: 0,
        cache_read_tokens: 0,
        cache_write_tokens: 0,
      }
      // usage arrives once per model step; accumulate
      return {
        ...state,
        usage: {
          input_tokens: prev.input_tokens + num(d.input_tokens),
          output_tokens: prev.output_tokens + num(d.output_tokens),
          cache_read_tokens: prev.cache_read_tokens + num(d.cache_read_tokens),
          cache_write_tokens: prev.cache_write_tokens + num(d.cache_write_tokens),
        },
      }
    }
    case "done":
      return {
        ...state,
        status: "completed",
        content: str(d.output) || state.content,
        cost: typeof d.cost === "number" ? d.cost : null,
        steps: num(d.steps),
        step: undefined,
        finished: true,
        toolCalls: state.toolCalls.map((c) => (c.status === "running" ? { ...c, status: "done" } : c)),
      }
    case "error": {
      const code = str(d.code)
      return {
        ...state,
        status: code === "cancelled" ? "cancelled" : "failed",
        error: { message: str(d.message), code },
        step: undefined,
        finished: true,
      }
    }
    case "paused":
      return { ...state, status: "paused", step: undefined, finished: true }
    case "lagged":
      return { ...state, skipped: state.skipped + num(d.skipped) }
    default:
      return state
  }
}

/**
 * Subscribe to a run's live events while `enabled`. Stops reconnecting once the
 * run finished (the server closes the stream after the terminal event).
 */
export function useRunStream(uid: string | undefined, enabled = true) {
  const [state, dispatch] = useReducer(runStreamReducer, initialRunStreamState)
  const [connection, setConnection] = useReducer(
    (_: SubscriptionStatus, s: SubscriptionStatus) => s,
    "closed" as SubscriptionStatus,
  )

  useEffect(() => {
    dispatch({ event: "reset" })
    if (!uid || !enabled) return
    let finished = false
    const sub = subscribeSSE(`/runs/${uid}/events`, {
      onEvent: (ev) => {
        dispatch({ event: ev.event, data: ev.json })
        if (["done", "error", "paused"].includes(ev.event)) {
          finished = true
          sub.close()
        } else if (ev.event === "snapshot") {
          const status = str(obj(ev.json).status)
          if (TERMINAL_STATUSES.has(status)) {
            finished = true
            sub.close()
          }
        }
      },
      onStatus: (s) => setConnection(finished && s !== "closed" ? "closed" : s),
      maxAttempts: 20,
    })
    return () => sub.close()
  }, [uid, enabled])

  return { ...state, connection }
}
